import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import type { Room } from '@peles/shared';

export interface RoomAutosuggestProps {
  /** Room name shown in the input (free text while typing). */
  value: string;
  /** Rooms of the event venue. */
  rooms: Room[];
  disabled?: boolean;
  /** Called with the next (name, roomId). roomId is null for free text. */
  onChange: (name: string, roomId: string | null) => void;
  /** When provided, offers an "add room to venue" row for unmatched text. */
  onCreateRoom?: (name: string) => void;
}

/**
 * Combobox for the plan-row room: type to search the venue's rooms, or type a
 * new name and add it as a room of the venue. Mirrors ItemAutosuggest /
 * VendorAutosuggest.
 */
export function RoomAutosuggest({
  value,
  rooms,
  disabled,
  onChange,
  onCreateRoom,
}: RoomAutosuggestProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredRooms = useMemo(() => {
    if (value.length < 1) return rooms;
    const lower = value.toLowerCase();
    return rooms.filter((r) => r.name.toLowerCase().includes(lower));
  }, [rooms, value]);

  // An exact (case-insensitive) match means the typed text already names an
  // existing room, so the "add" row is suppressed.
  const hasExactMatch = useMemo(
    () => rooms.some((r) => r.name.trim().toLowerCase() === value.trim().toLowerCase()),
    [rooms, value],
  );
  const canCreate = !!onCreateRoom && value.trim().length > 0 && !hasExactMatch;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value, null);
      setIsOpen(true);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleSelect = useCallback(
    (room: Room) => {
      onChange(room.name, room.id);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setIsOpen(true);
  }, []);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
      setHighlightedIndex(-1);
    }, 200);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;
      const totalItems = filteredRooms.length + (canCreate ? 1 : 0);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredRooms.length) {
          handleSelect(filteredRooms[highlightedIndex]);
        } else if (highlightedIndex === filteredRooms.length && canCreate) {
          onCreateRoom!(value.trim());
          setIsOpen(false);
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    },
    [isOpen, filteredRooms, highlightedIndex, handleSelect, canCreate, onCreateRoom, value],
  );

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const lis = dropdownRef.current.querySelectorAll('li');
      const li = lis[highlightedIndex];
      if (li && typeof li.scrollIntoView === 'function') li.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const showDropdown = isOpen && !disabled && (filteredRooms.length > 0 || canCreate);

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="Search rooms or type a new one"
        aria-label="Room"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls="room-autosuggest-list"
        autoComplete="off"
        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
      />

      {showDropdown && (
        <ul
          id="room-autosuggest-list"
          ref={dropdownRef}
          role="listbox"
          className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredRooms.map((room, index) => (
            <li
              key={room.id}
              role="option"
              aria-selected={highlightedIndex === index}
              className={`px-3 py-2 cursor-pointer text-sm ${
                highlightedIndex === index
                  ? 'bg-primary-50 text-primary-900'
                  : 'hover:bg-gray-50 text-gray-900'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(room);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {room.name}
            </li>
          ))}
          {canCreate && (
            <li
              role="option"
              aria-selected={highlightedIndex === filteredRooms.length}
              className={`px-3 py-2 cursor-pointer text-sm border-t border-gray-100 ${
                highlightedIndex === filteredRooms.length
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-primary-600 hover:bg-gray-50'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                onCreateRoom!(value.trim());
                setIsOpen(false);
              }}
              onMouseEnter={() => setHighlightedIndex(filteredRooms.length)}
            >
              + Add room &ldquo;{value.trim()}&rdquo; to the venue
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
